import { cache } from 'react';
import { normalizeProximity } from './proximity';
import type {
  City,
  Listing,
  Photo,
  SearchParams,
  SearchResult,
  SitemapListing,
} from '@/types/listing';
import type {
  Favorite,
  TourRequest,
  CreateTourRequest,
  AvailableSlots,
  SavedSearch,
} from '@/types/auth';
import type { Decision, ShareComment, SharedHomeView } from './sharedHome';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';

interface ApiOptions extends RequestInit {
  /** Next.js fetch cache options — pass { revalidate } from server components for ISR. */
  next?: { revalidate?: number | false; tags?: string[] };
  /** Set true to require an auth token (throws when signed out). */
  auth?: boolean;
}

/**
 * Base fetch with optional Firebase auth.
 * In the browser, a token is attached only when a session flag exists, so the
 * Firebase SDK is never loaded for anonymous visitors. On the server no token
 * is attached (all server-rendered data is public).
 */
/**
 * Delay before retrying a transient failure. Honors a numeric Retry-After
 * (seconds) capped at 5s so a misbehaving header can't hang the UI;
 * HTTP-date form and junk fall back to the 400ms default.
 */
export function retryDelayMs(retryAfter: string | null): number {
  const seconds = Number(retryAfter);
  if (!retryAfter || !Number.isFinite(seconds) || seconds <= 0) return 400;
  return Math.min(seconds, 5) * 1000;
}

async function fetchAPI<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { auth: requireAuth, next, headers: extraHeaders, ...init } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string> | undefined),
  };

  if (typeof window !== 'undefined') {
    const { getAuthToken } = await import('@/lib/auth');
    const token = await getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else if (requireAuth) {
      throw new Error('Authentication required');
    }
  } else if (requireAuth) {
    throw new Error('Authentication required');
  }

  // Correlates a browser failure with the API's request logs.
  const requestId =
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '';
  if (requestId) headers['X-Request-ID'] = requestId;

  const doFetch = () =>
    fetch(`${API_URL}${endpoint}`, {
      ...init,
      ...(next ? { next } : {}),
      headers,
    });

  // Idempotent GETs get ONE retry on transient failures (network blip,
  // gateway 502/503/504 during deploys). 500 is included too: a cold Cloud Run
  // instance whose upstream (e.g. the Walk Score call behind /scores) times out
  // returns 500 on the first hit and 200 on the retry — the exact signature we
  // saw on the listing detail's secondary data. Retrying an idempotent GET is
  // side-effect-free, so this recovers the real data instead of swallowing it.
  // Mutations never retry.
  const isGet = !init.method || init.method === 'GET';
  let res: Response;
  try {
    res = await doFetch();
  } catch (err) {
    if (!isGet) throw err;
    await new Promise((r) => setTimeout(r, 400));
    res = await doFetch();
  }
  if (isGet && [429, 500, 502, 503, 504].includes(res.status)) {
    await new Promise((r) => setTimeout(r, retryDelayMs(res.headers?.get?.('Retry-After') ?? null)));
    res = await doFetch();
  }

  if (!res.ok) {
    const error = await res.text();
    // `status` rides along because some callers must tell one failure from
    // another rather than collapsing them into "something went wrong".
    // `/me/resend-verification` is the case that forced it: 502 means the mail
    // provider REFUSED the message and 503 means the flow is not wired at all,
    // and both must be reported as "no email was sent" in words the reader can
    // act on — never as the cheerful "check your inbox" that a status-blind
    // catch-all produces.
    throw Object.assign(new Error(error || `API error: ${res.status}`), {
      requestId,
      status: res.status,
    });
  }

  // DELETEs return 204/empty bodies; res.json() on those throws
  // "Unexpected end of JSON input" AFTER the operation already succeeded.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ============================================
// Search & Listings
// ============================================

export async function searchListings(
  params: SearchParams,
  init?: { signal?: AbortSignal }
): Promise<SearchResult> {
  // Normalised at the one place a search leaves the app: `sort=nearest` without a
  // `near` origin is a 400 on the entire search, and `sort` round-trips through
  // the URL while `near` does not.
  params = normalizeProximity(params);

  const body = {
    query: params.q || undefined,
    bbox: params.bbox
      ? {
          top_left: { lat: params.bbox.topLeft.lat, lon: params.bbox.topLeft.lon },
          bottom_right: { lat: params.bbox.bottomRight.lat, lon: params.bbox.bottomRight.lon },
        }
      : undefined,
    price_min: params.priceMin,
    price_max: params.priceMax,
    beds_min: params.bedsMin,
    baths_min: params.bathsMin,
    sqft_min: params.sqftMin,
    sqft_max: params.sqftMax,
    property_types: params.propertyTypes,
    cities: params.cities,
    year_built_min: params.yearBuiltMin,
    year_built_max: params.yearBuiltMax,
    light: params.light || undefined,
    open_houses_only: params.openHousesOnly || undefined,
    price_cuts_only: params.priceCutsOnly || undefined,
    hoa_max: params.hoaMax,
    dom_max: params.domMax,
    zips: params.zips,
    near: params.near ? { lat: params.near.lat, lon: params.near.lon } : undefined,
    sort: params.sort,
    page: params.page || 0,
    per_page: params.perPage || 20,
  };

  return fetchAPI<SearchResult>('/api/v1/search', {
    method: 'POST',
    body: JSON.stringify(body),
    signal: init?.signal,
  });
}

/**
 * The same search, at most once per request.
 *
 * `/homes` needs its result set twice on the server: `generateMetadata` wants
 * the total for the tab title, and the page body wants the cards. React's
 * `cache` lives for one request, so the second caller joins the first's promise
 * instead of issuing a second POST — verified by the API's own request logs
 * carrying one search per page load, not two.
 *
 * Keyed on the serialized params because `cache` compares arguments by
 * identity and both callers build a fresh object. A failure resolves to `null`
 * (both callers already treat a failed search as "no data yet", and the title
 * then simply omits the count) — so one failure is shared too, rather than
 * retried a second time on the same page.
 */
const searchListingsByKey = cache(
  async (paramsJson: string): Promise<SearchResult | null> =>
    searchListings(JSON.parse(paramsJson) as SearchParams).catch(() => null)
);

export function searchListingsForRequest(params: SearchParams): Promise<SearchResult | null> {
  return searchListingsByKey(JSON.stringify(params));
}

/**
 * Fill in photos the search index hasn't caught up on, before the HTML is sent.
 *
 * `/api/v1/search` reads Meilisearch, which lags the database on freshly
 * ingested homes: `photos` comes back empty next to `photoCount: 51`. Measured
 * 2026-07-24 — 6 of 240 results, *all* of them at the top of the default
 * "Newest" sort, i.e. the cards above the fold on `/homes`.
 *
 * `ListingCard` also repairs this client-side (see `useListingPhotos`), which
 * is what covers filter changes and scrolling. Doing the top cards on the
 * server as well matters for one specific reason: the LCP image. Left to the
 * client, the hero photo cannot even start downloading until a round-trip to
 * `/listings/{id}` has finished — measured **LCP 1468 ms** on a search page
 * whose first four cards were all index-lagged, against 236 ms when the URL is
 * already in the HTML.
 *
 * Costs nothing on the overwhelmingly common path: with no gaps in the first
 * `limit` cards it issues no requests at all.
 */
export async function withBackfilledPhotos(
  result: SearchResult | null,
  limit = 6
): Promise<SearchResult | null> {
  if (!result?.listings?.length) return result;

  const gaps = result.listings
    .slice(0, limit)
    .filter((l) => (l.photos?.length ?? 0) === 0 && (l.photoCount ?? 0) > 0);
  if (gaps.length === 0) return result;

  const filled = await Promise.all(
    gaps.map((l) =>
      getListing(l.id)
        .then((full) => [l.id, full.photos ?? []] as const)
        // A backfill that fails must not take the search page down with it —
        // the card still renders, just without its photos.
        .catch(() => [l.id, [] as Photo[]] as const)
    )
  );
  const byId = new Map(filled);

  return {
    ...result,
    listings: result.listings.map((l) => {
      const photos = byId.get(l.id);
      return photos?.length ? { ...l, photos } : l;
    }),
  };
}

export async function getListing(id: string): Promise<Listing> {
  return fetchAPI<Listing>(`/api/v1/listings/${id}`, { next: { revalidate: 300 } });
}

/**
 * Ids per `/listings/batch` request. The API caps at 100 and silently drops the
 * rest, so the client must chunk at the same number rather than lose homes.
 */
export const BATCH_ID_LIMIT = 100;

/**
 * Hydrate many listings in one request.
 *
 * The favorites and compare pages used to fetch `/listings/{id}` once per saved
 * home — a network N+1 that grew with the shortlist (30 favorites = 31
 * requests). Measured against the live API 2026-07-24: 8 sequential GETs
 * 1157 ms vs one batch 193 ms (**6.0×**); at 30 ids the browser also stops
 * queueing behind its own per-origin connection limit.
 *
 * Contract, matching the handler: ids are deduped, order is the order asked,
 * ids that no longer resolve are **omitted** (a favorite whose home sold is not
 * a client error). A batch that fails falls back to the per-id path it
 * replaced, so an older API revision — where `/listings/batch` 404s as if
 * `batch` were an id — degrades to working-but-slower instead of an empty page.
 *
 * **An omission is a claim here, so only a 404 may cause one.**
 *
 * Every caller reads a missing id as news about the home. `/favorites` prints
 * *"No longer available — This home has been removed from the MLS catalog"* over
 * the card and *"3 are no longer on the market"* in the header; `/compare` says
 * *"These homes aren't listed any more, so there's nothing left to compare."*
 * The fallback used to end `.catch(() => null)` per id, so a 500 on
 * `/listings/{id}` — or the listing service being down entirely, which drops the
 * batch AND every per-id retry — published all of that as fact. A buyer's own
 * shortlist, declared deleted, because a server was restarting.
 *
 * The API draws the line for us and this threw it away: `GET /listings/{id}`
 * answers **404 "listing not found"** for a row that is gone and **500 "failed
 * to load listing"** when the lookup itself failed
 * (`internal/api/handlers/listing.go` `Get`, whose own comment says answering
 * 404 to both "hides it from every uptime check"). So 404 → omit, anything else
 * → **reject**, and the pages above render their error state instead of an
 * obituary. `fetchAPI` has already retried the 500 once by this point.
 */
export async function getListingsByIds(ids: string[]): Promise<Listing[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH_ID_LIMIT) {
    chunks.push(unique.slice(i, i + BATCH_ID_LIMIT));
  }

  const byId = new Map<string, Listing>();
  await Promise.all(
    chunks.map(async (chunk) => {
      let listings: Listing[];
      try {
        listings = await fetchAPI<Listing[] | null>(
          `/api/v1/listings/batch?ids=${chunk.map(encodeURIComponent).join(',')}`,
          { next: { revalidate: 300 } }
        ).then((v) => v ?? []);
      } catch {
        // The batch route is unreachable on this revision (or right now).
        // Ask per id — and keep the 404/everything-else distinction, because a
        // silent omission here is rendered as "this home has been removed from
        // the MLS catalog".
        listings = (
          await Promise.all(
            chunk.map((id) =>
              getListing(id).catch((err: unknown) => {
                if ((err as { status?: number } | null)?.status === 404) return null;
                throw err;
              })
            )
          )
        ).filter((l): l is Listing => l !== null);
      }
      for (const l of listings) byId.set(l.id, l);
    })
  );

  return unique
    .map((id) => byId.get(id))
    .filter((l): l is Listing => l !== undefined);
}

/**
 * Could this string be an MLS listing id at all?
 *
 * The listing route forwards whatever sits in its `[id]` segment straight to the
 * API, so anything a crawler invents becomes an upstream lookup. The single
 * biggest source of 4xx on this service is exactly that: 148 requests a day for
 * `/api/v1/listings/mls/sms:6506678000` — a phone number, from crawlers
 * resolving an `sms:` href relative to the page they found it on.
 *
 * Alphanumeric and short. Every live listing id is eight digits; fixtures and
 * the RETS mapper also carry an `ML`-prefixed form, so letters stay allowed and
 * only the shape is checked. Deliberately not a stricter pattern: refusing a
 * real id would 404 a real home, which is far worse than one wasted lookup.
 */
export function isPlausibleMlsId(id: string): boolean {
  return /^[A-Za-z0-9]{4,20}$/.test(id);
}

export async function getListingByMlsId(mlsId: string): Promise<Listing> {
  if (!isPlausibleMlsId(mlsId)) {
    // Thrown rather than returning null so the caller's existing catch renders
    // the same not-found page it always did — the difference is that no request
    // leaves this process.
    throw new Error(`not an MLS listing id: ${mlsId}`);
  }
  return fetchAPI<Listing>(`/api/v1/listings/mls/${mlsId}`, { next: { revalidate: 300 } });
}

/** Per-request deduped variant for server pages (generateMetadata + page body share one fetch). */
export const getListingByMlsIdCached = cache(getListingByMlsId);

export async function getAllCities(): Promise<City[]> {
  return fetchAPI<City[] | null>('/api/v1/cities', { next: { revalidate: 3600 } }).then(
    (v) => v ?? []
  );
}

export const getAllCitiesCached = cache(getAllCities);

/** Homes in one city, 20 per page. */
export const CITY_PAGE_SIZE = 20;

/**
 * `/cities/{slug}/listings` pages by **`limit`/`offset`**, not `page`. This sent
 * `?page=N`, which the handler never reads — so every page after the first
 * returned the *same* twenty homes (verified live: pages 0–5 of San Jose gave
 * 120 rows and 20 unique ids). Nothing in the app paginated it yet, so no page
 * shipped the duplicate, but the argument was a lie waiting for a caller.
 */
export async function getCityListings(citySlug: string, page = 0): Promise<SearchResult> {
  const offset = Math.max(0, Math.trunc(page)) * CITY_PAGE_SIZE;
  return fetchAPI<SearchResult>(
    `/api/v1/cities/${citySlug}/listings?limit=${CITY_PAGE_SIZE}&offset=${offset}`,
    { next: { revalidate: 900 } }
  );
}

/**
 * Why these homes came back — so the section can describe them truthfully.
 *
 * `sameCity` is the load-bearing one: the endpoint widens to a radius around the
 * subject when the city itself has nothing comparable, and a heading that still
 * said "Similar homes in San Jose" over a home in Campbell would be a plain lie.
 */
export interface SimilarBasis {
  /** Which band answered: 1 tightest, 3 widest. */
  tier: number;
  priceMin: number;
  priceMax: number;
  /** Whether the results were ordered by distance (false for an ungeocoded subject). */
  usedGeo: boolean;
  /** Whether the search was still restricted to the subject's city. */
  sameCity: boolean;
  /** Present only once the city was traded for a geographic box. */
  radiusKm?: number;
}

/**
 * Homes comparable to this one, from the SEARCH INDEX.
 *
 * Replaces a `getCityListings` call that was never a comparables query: its SQL
 * is `ORDER BY updated_at DESC`, and ingestion sets `updated_at = NOW()`
 * unconditionally, so those 20 rows were whichever ones the last hourly run wrote
 * last — measured live, sharing 0 of 20 ids with the true newest 20, spanning
 * list dates from January to July. They were then filtered on price alone: no
 * bedroom, bath, sqft, property-type or distance criterion at all.
 *
 * Same `revalidate` as the call it replaces, so the detail page stays ISR and the
 * request count per render is unchanged.
 *
 * The endpoint answers **503, not an empty list**, when its index is unavailable,
 * so a rejection here is a real failure and the caller renders nothing.
 */
export async function getSimilarListings(
  id: string
): Promise<{ listings: Listing[]; basis: SimilarBasis }> {
  return fetchAPI<{ listings: Listing[]; basis: SimilarBasis }>(
    `/api/v1/listings/${id}/similar`,
    { next: { revalidate: 900 } }
  );
}

export async function getSitemapListings(): Promise<SitemapListing[]> {
  return fetchAPI<SitemapListing[]>('/api/v1/sitemap/listings', {
    next: { revalidate: 3600 },
  });
}

export async function autocomplete(
  query: string,
  init?: { signal?: AbortSignal }
): Promise<string[]> {
  if (query.length < 2) return [];
  // API can return JSON null for empty result sets on older revisions.
  return fetchAPI<string[] | null>(`/api/v1/autocomplete?q=${encodeURIComponent(query)}`, {
    signal: init?.signal,
  }).then((v) => v ?? []);
}

/** A typed, city-first search suggestion from `/api/v1/search/suggest`. */
export interface Suggestion {
  type: 'city' | 'zip' | 'address';
  /** Filter-ready value: a lowercased city name, a ZIP, or an address string. */
  value: string;
  /** Active-listing count (city and ZIP rows). */
  count?: number;
  /** Ids behind an address row so a tap can open the listing directly. */
  listingId?: string;
  mlsId?: string;
}

/**
 * City-first typed suggestions: cities (with listing counts) first, then ZIPs,
 * then addresses. Distinct from `autocomplete` (bare address strings).
 *
 * NOTE: `/search/suggest` ships with this change and may 404 until the API
 * deploy — callers should fall back to `autocomplete` on rejection.
 */
export async function suggest(
  query: string,
  init?: { signal?: AbortSignal }
): Promise<Suggestion[]> {
  if (query.length < 2) return [];
  return fetchAPI<Suggestion[] | null>(`/api/v1/search/suggest?q=${encodeURIComponent(query)}`, {
    signal: init?.signal,
  }).then((v) => v ?? []);
}

export interface ListingScores {
  scores?: { walk?: number; transit?: number; bike?: number };
  schools?: Array<{ name: string; rating?: number; level?: string; distance?: number }>;
}

export async function getListingScores(listingId: string): Promise<ListingScores> {
  return fetchAPI<ListingScores>(`/api/v1/listings/${listingId}/scores`, {
    next: { revalidate: 86400 },
  });
}

export interface PriceEvent {
  id: string;
  listing_id: string;
  price: number;
  changed_at: string;
}

/**
 * Recorded price changes for a listing, oldest first. Captured forward-only by
 * the backend (migration 010), so listings with no tracked moves return `[]`.
 *
 * **It used to end `.catch(() => [])`, and that turned an outage into a fact
 * about the home.** `[]` is what `PriceJourney` reads as "this home has
 * no recorded price changes" — a sentence about the property — so a failed
 * request published it as one.
 *
 * The endpoint's own shapes say this is not a theoretical distinction
 * (`internal/api/handlers/listing.go` `PriceHistory`, probed live 2026-08-03
 * against staging):
 *
 *   GET /listings/{real id}/price-history                    → 200 []   (no moves)
 *   GET /listings/513d5919-…/price-history                   → 200 [3 events]
 *   GET /listings/00000000-0000-0000-0000-000000000000/…     → 200 []
 *   GET /listings/not-a-uuid/price-history                   → 200 []
 *
 * It never 404s — an unknown id is 200 `[]` — and the only non-2xx it produces
 * is **500**: `priceEvents == nil` (the repository unwired) or the query
 * failing. So a rejection here means "we could not ask", never "there is
 * nothing", and the caller must render the two differently.
 * `fetchAPI` has already retried the 500 once by this point.
 */
export async function getPriceHistory(listingId: string): Promise<PriceEvent[]> {
  return fetchAPI<PriceEvent[] | null>(`/api/v1/listings/${listingId}/price-history`, {
    next: { revalidate: 3600 },
  }).then((v) => v ?? []);
}

export interface CommuteResult {
  destination_id: string;
  label: string;
  address: string;
  driving_minutes?: number;
  transit_minutes?: number;
}

export async function getListingCommutes(listingId: string): Promise<CommuteResult[]> {
  return fetchAPI<CommuteResult[] | null>(`/api/v1/listings/${listingId}/commutes`, {
    auth: true,
  }).then((v) => v ?? []);
}

// ============================================
// Account
// ============================================

/**
 * Permanently delete the signed-in account.
 *
 * `DELETE /api/v1/me` takes no body and no path parameter — the id comes from
 * the verified token and nowhere else (`internal/api/handlers/user.go`
 * `DeleteAccount`). Server-side it is one transaction: `tour_requests` and
 * `notification_logs` are anonymised (the business record survives without the
 * buyer's contact details), then the `users` row is deleted and favorites,
 * saved searches, destinations, device tokens and alert settings cascade with
 * it. Deleting an id that is already gone is a no-op, not an error, so a
 * double-tapped confirm cannot 500 at somebody mid-deletion.
 *
 * It exists because the only route out used to be a mailto address and a
 * promise — a broken commitment on `/privacy`, and an App Store Guideline
 * 5.1.1(v) submission blocker. The web said "email us" for months after the
 * endpoint went live.
 */
export async function deleteAccount(): Promise<void> {
  await fetchAPI<void>('/api/v1/me', {
    method: 'DELETE',
    auth: true,
  });
}

/**
 * What asking for a verification link actually resulted in.
 *
 * Five outcomes, deliberately NOT collapsed into a boolean. Alert email is
 * gated on `users.email_verified`, so a buyer stuck on the wrong side of that
 * gate receives nothing at all however many searches they save — and telling
 * them "check your inbox" when no message left the building is the exact way
 * this project has repeatedly turned a broken feature into an invisible one.
 *
 * - `sent` — the provider ACCEPTED the message. The only state that may say
 *   "check your inbox".
 * - `already-verified` — a 200, but `sent: false`. The address is already
 *   verified and nothing was emailed; saying "check your inbox" here sends
 *   somebody to hunt for mail that does not exist.
 * - `provider-refused` — 502. The token was minted and the send FAILED.
 * - `not-configured` — 503. The flow is unwired server-side; a deployment
 *   fault, not a user error, and no amount of retrying fixes it.
 * - `failed` — anything else (401, 404, 500, a network drop).
 */
export type ResendVerificationResult =
  | { status: 'sent' }
  | { status: 'already-verified' }
  | { status: 'provider-refused' }
  | { status: 'not-configured' }
  | { status: 'failed' };

/**
 * Ask the API to email a fresh verification link to the address on the account.
 *
 * `POST /api/v1/me/resend-verification` — note **`/me`**, not `/auth`: the
 * route sits inside the authenticated `/me` group
 * (`internal/api/router.go`), because it emails the address on file and so the
 * caller must already own the session. It takes no body; the user id comes from
 * the verified token and nowhere else.
 *
 * **Resolves rather than throws**, because every one of its failures is
 * something a human has to be told in words — a rejected promise at this call
 * site is how "we couldn't do it" becomes a silent no-op or, worse, a success
 * banner rendered by a `finally`.
 *
 * **`sent` is believed only when the server says `sent === true`.** A 200 whose
 * body is missing, truncated or shaped unexpectedly resolves to `failed`, not
 * to success: the default answer to "did the email go?" must be "we cannot say
 * it did".
 */
export async function resendVerificationEmail(): Promise<ResendVerificationResult> {
  let body: { email_verified?: boolean; sent?: boolean } | undefined;
  try {
    body = await fetchAPI<{ email_verified?: boolean; sent?: boolean } | undefined>(
      '/api/v1/me/resend-verification',
      { method: 'POST', auth: true }
    );
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    if (status === 502) return { status: 'provider-refused' };
    if (status === 503) return { status: 'not-configured' };
    return { status: 'failed' };
  }

  // Order matters: `{email_verified: true, sent: false}` is a SUCCESSFUL 200
  // that sent nothing, so it must be read before anything infers "sent" from
  // the 2xx alone.
  if (body?.sent === true) return { status: 'sent' };
  if (body?.email_verified === true) return { status: 'already-verified' };
  return { status: 'failed' };
}

/**
 * What happened when a verification link was opened.
 *
 * `invalid` deliberately covers unknown, expired AND already-used, because the
 * server answers all three with one message on purpose — distinguishing them
 * turns the endpoint into an oracle for guessing tokens
 * (`UserHandler.VerifyEmail`). The UI must not invent a distinction the server
 * refused to make.
 */
export type VerifyEmailResult =
  | { status: 'verified' }
  | { status: 'invalid' }
  | { status: 'not-configured' }
  | { status: 'failed' };

/**
 * Redeem the token from a verification email.
 *
 * `POST /api/v1/auth/verify-email` is **unauthenticated on purpose**: the link
 * is opened from an inbox, on whichever device is to hand, and requiring a
 * session there would fail exactly the people who have not got one. The token
 * is the credential — so this is the one `/auth` call that must NOT set
 * `auth: true`, or opening the link in a browser that is signed out throws
 * "Authentication required" before the request is even made.
 *
 * Resolves for every outcome, for the same reason as
 * `resendVerificationEmail`: each one is something the reader has to be told.
 * A 200 is believed only when the body says `email_verified === true`.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResult> {
  let body: { email_verified?: boolean } | undefined;
  try {
    body = await fetchAPI<{ email_verified?: boolean } | undefined>('/api/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    if (status === 400) return { status: 'invalid' };
    if (status === 503) return { status: 'not-configured' };
    return { status: 'failed' };
  }
  return body?.email_verified === true ? { status: 'verified' } : { status: 'failed' };
}

/**
 * The outcome of redeeming a password-reset link.
 *
 * Same five-way shape as verification, for the same reason: a page that says
 * "your password is changed" when the server refused is worse than an error.
 */
export type ResetPasswordResult =
  | { status: 'reset' }
  | { status: 'invalid' }        // expired, already used, or never real
  | { status: 'weak-password' }  // under the server's 8-character floor
  | { status: 'not-configured' } // the flow is unwired: a deployment fault
  | { status: 'failed' };

/**
 * Redeem a reset token and set a new password.
 *
 * **The page this is called from did not exist.** `internal/api/handlers/user.go:283`
 * mints `{siteURL}/reset-password?token=…` and emails it, and there was no such
 * route in the web app — so every password-reset link the API has ever sent
 * landed on the 404 page. The web's own reset goes through Firebase, which is
 * why nobody noticed: the broken path is the one iOS and the API use.
 *
 * Resolves rather than rejects, so the caller renders an outcome instead of
 * catching an exception it has to re-classify.
 */
export async function resetPassword(token: string, newPassword: string): Promise<ResetPasswordResult> {
  try {
    await fetchAPI<void>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    // The server answers 400 for BOTH a bad token and a short password, so the
    // message is the only thing that separates them. Match on what it actually
    // says rather than guessing which the user hit.
    if (status === 400) {
      const message = (err as { message?: string } | null)?.message ?? '';
      return /password must be at least/i.test(message)
        ? { status: 'weak-password' }
        : { status: 'invalid' };
    }
    if (status === 503) return { status: 'not-configured' };
    return { status: 'failed' };
  }
  return { status: 'reset' };
}

// ============================================
// Favorites
// ============================================

/**
 * In-flight `/favorites` request, shared by concurrent callers.
 *
 * Two independent consumers ask the moment a session resolves: the header's
 * `AuthMenu` (which needs the id set so every heart on the page renders in the
 * right state) and, on `/favorites`, the page's own query (which needs the notes
 * too). They fired in the same millisecond — measured, two GETs per favorites
 * page load. Coalescing hands both the one response they would each have got.
 *
 * Cleared as soon as the request settles, and explicitly on every favorites
 * mutation, so a read that follows a write can never join a flight that started
 * before it.
 */
let favoritesInFlight: Promise<Favorite[]> | null = null;

export async function getFavorites(): Promise<Favorite[]> {
  if (favoritesInFlight) return favoritesInFlight;
  favoritesInFlight = fetchAPI<Favorite[] | null>('/api/v1/favorites', { auth: true })
    .then((v) => v ?? [])
    .finally(() => {
      favoritesInFlight = null;
    });
  return favoritesInFlight;
}

export async function addFavorite(listingId: string, notes?: string): Promise<Favorite> {
  favoritesInFlight = null;
  return fetchAPI<Favorite>('/api/v1/favorites', {
    method: 'POST',
    body: JSON.stringify({ listing_id: listingId, notes }),
    auth: true,
  });
}

export async function removeFavorite(listingId: string): Promise<void> {
  favoritesInFlight = null;
  await fetchAPI<void>(`/api/v1/favorites/${listingId}`, {
    method: 'DELETE',
    auth: true,
  });
}

export async function isFavorite(listingId: string): Promise<boolean> {
  try {
    const favorites = await getFavorites();
    return favorites.some((f) => f.listing_id === listingId);
  } catch {
    return false;
  }
}

// ============================================
// Tours
// ============================================

export async function requestTour(data: CreateTourRequest): Promise<TourRequest> {
  return fetchAPI<TourRequest>('/api/v1/tours', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTours(): Promise<TourRequest[]> {
  return fetchAPI<TourRequest[] | null>('/api/v1/tours', { auth: true }).then((v) => v ?? []);
}

/**
 * Get available time slots for a listing on a specific date.
 * Route is /api/v1/tours/slots (the old /listings/{id}/available-slots path 404'd).
 */
export async function getAvailableSlots(listingId: string, date: string): Promise<AvailableSlots> {
  // The endpoint returns a bare array of time strings; older clients typed
  // {date, slots} — normalize to the object shape either way.
  const res = await fetchAPI<unknown>(
    `/api/v1/tours/slots?listing_id=${encodeURIComponent(listingId)}&date=${encodeURIComponent(date)}`
  );
  const slots = Array.isArray(res)
    ? (res as string[])
    : ((res as AvailableSlots)?.slots ?? []);
  return { date, slots };
}

export async function cancelTour(tourId: string): Promise<void> {
  await fetchAPI<void>(`/api/v1/tours/${tourId}`, {
    method: 'DELETE',
    auth: true,
  });
}

// ============================================
// Saved Searches
// ============================================

export async function getSavedSearches(): Promise<SavedSearch[]> {
  return fetchAPI<SavedSearch[] | null>('/api/v1/saved-searches', { auth: true }).then((v) => v ?? []);
}

export async function createSavedSearch(
  name: string,
  filters: Record<string, unknown>,
  notify?: boolean
): Promise<SavedSearch> {
  return fetchAPI<SavedSearch>('/api/v1/saved-searches', {
    method: 'POST',
    body: JSON.stringify({ name, filters, notify }),
    auth: true,
  });
}

export async function updateSavedSearch(
  id: string,
  patch: { name?: string; notify?: boolean }
): Promise<SavedSearch> {
  return fetchAPI<SavedSearch>(`/api/v1/saved-searches/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
    auth: true,
  });
}

export async function deleteSavedSearch(id: string): Promise<void> {
  await fetchAPI<void>(`/api/v1/saved-searches/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

// ============================================
// Destinations (commute targets)
// ============================================

export interface Destination {
  id: string;
  label: string;
  address: string;
  latitude: number;
  longitude: number;
  created_at: string;
}

export async function getDestinations(): Promise<Destination[]> {
  return fetchAPI<Destination[] | null>('/api/v1/me/destinations', { auth: true }).then(
    (v) => v ?? []
  );
}

export async function addDestinationApi(
  label: string,
  address: string,
  latitude: number,
  longitude: number
): Promise<Destination> {
  return fetchAPI<Destination>('/api/v1/me/destinations', {
    method: 'POST',
    body: JSON.stringify({ label, address, latitude, longitude }),
    auth: true,
  });
}

export async function removeDestinationApi(id: string): Promise<void> {
  await fetchAPI<void>(`/api/v1/me/destinations/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

// ============================================
// Market stats
// ============================================

export interface MarketStats {
  city: string;
  region: string;
  active_total: number;
  sample_size: number;
  median_price: number;
  p25_price: number;
  p75_price: number;
  median_ppsf: number;
  median_sqft: number;
  median_dom: number;
  price_cut_share: number;
  new_share: number;
}

/**
 * City market stats.
 *
 * **`null` means the server ANSWERED and declined to describe this market** —
 * `GetCity` replies 404 "not enough active listings for market stats" both for a
 * city under `markets.MinSample` = 20 and for a slug it has never heard of
 * (`internal/api/handlers/markets.go`). That is a fact, and `/homes/[city]`
 * publishes it as one: `cityCoverage` turns a null into *"We list 851 listings
 * in this city right now … we don't publish a median until we hold at least 20
 * homes here."*
 *
 * **It used to end `.catch(() => null)`, so every other failure was published as
 * that same sentence.** A 500, a timeout or a dropped connection on
 * `/markets/san-jose` printed "we hold fewer than 20 homes here" over 851 of
 * them — a claim that contradicts the count in the line before it, built
 * entirely out of a failed request.
 *
 * So only a **404** resolves to `null`. Everything else rejects, and the caller
 * must decide what a page may say when it could not ask. `fetchAPI` has already
 * retried a 5xx once by this point.
 *
 * (The server conflates one case we cannot separate from here: `computeCity`
 * returns `ok=false` when its own search errors, which `GetCity` also answers
 * 404. That is a backend defect — reported, not worked around here.)
 */
export async function getMarketStats(citySlug: string): Promise<MarketStats | null> {
  return fetchAPI<MarketStats>(`/api/v1/markets/${citySlug}`, {
    next: { revalidate: 3600 },
  }).catch((err: unknown) => {
    if ((err as { status?: number } | null)?.status === 404) return null;
    throw err;
  });
}

/**
 * The covered cities with their stats — the top 40 by inventory
 * (`marketIndexMax` in `internal/api/handlers/markets.go`), each holding at
 * least `markets.MinSample` = 20 active listings.
 *
 * **It used to end `.catch(() => [])`.** `/markets` reads an empty array as
 * "nothing to report" and prints *"Market stats are refreshing — check back
 * shortly."* — which, on the one page whose entire subject is what the Bay Area
 * market is doing, published an API outage as a statement about the market.
 *
 * The endpoint distinguishes the two itself and this wrapper threw the
 * distinction away: `ListMarkets` answers **503 "market stats unavailable"**
 * when `buildIndex` fails, and a 200 body is a real (possibly empty) index. So
 * a rejection means "we could not ask" and callers must say which.
 * `fetchAPI` has already retried the 503 once by this point.
 *
 * Callers that use this purely for *navigation* (the city page's nearby-city
 * links, the home feed's spotlight rail) may still collapse a failure to `[]` at
 * their own call site: a missing set of links is an absence, not a claim.
 */
export async function getMarkets(): Promise<MarketStats[]> {
  return fetchAPI<MarketStats[] | null>('/api/v1/markets', {
    next: { revalidate: 3600 },
  }).then((v) => v ?? []);
}

// ============================================
// Sharing one home with somebody
// ============================================

/**
 * What opening a share link produced.
 *
 * **Three failures, three different sentences**, because the API went to the
 * trouble of separating them and a client that collapses them tells the reader
 * the wrong thing about somebody else's house
 * (`internal/api/handlers/share.go`):
 *
 * - `link-dead` — **404**. The token never existed or the owner revoked it. One
 *   status covers both on purpose: telling a token-guesser that they found a
 *   real-but-revoked link is the only useful signal such an attack can get.
 * - `home-gone` — **410**. The link is perfectly good and the *house* has left
 *   the market. The handler's own comment says why this is not a 404: that would
 *   read as "your friend sent you a broken link", and blame the wrong person.
 * - `failed` — anything else, including a 500 (`fetchAPI` has already retried it
 *   once) and a dropped connection. "We could not ask", never "there is
 *   nothing".
 *
 * Resolves rather than throws for the same reason `verifyEmail` does: every one
 * of these is something a human has to be told in words, and a rejected promise
 * at the call site is how that becomes a blank page.
 */
export type SharedHomeResult =
  | { status: 'ok'; view: SharedHomeView }
  | { status: 'link-dead' }
  | { status: 'home-gone' }
  | { status: 'failed' };

/**
 * The shared house and its conversation. **No auth: the token is the
 * authorisation**, and requiring an account here would fail exactly the people
 * the feature exists for.
 *
 * `no-store` because a share is a live thread — a reaction posted a second ago
 * has to be in the next render, and a cached copy of somebody else's
 * conversation is the one thing this page must never serve.
 */
export async function getSharedHome(token: string): Promise<SharedHomeResult> {
  let view: SharedHomeView | undefined;
  try {
    view = await fetchAPI<SharedHomeView | undefined>(
      `/api/v1/shares/${encodeURIComponent(token)}`,
      { cache: 'no-store' }
    );
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    if (status === 404) return { status: 'link-dead' };
    if (status === 410) return { status: 'home-gone' };
    return { status: 'failed' };
  }
  // A 200 whose body is missing or shaped unexpectedly is a failure, not an
  // empty house: the page would otherwise render a home with no address, no
  // price and no photos as though that were the listing.
  if (!view?.listing?.id) return { status: 'failed' };
  return {
    status: 'ok',
    // `decisions` and `comments` are `[]` from a Go handler that always
    // allocates them, but this payload is also what an older revision or a
    // proxy might truncate, and `.map` on undefined takes the whole page down.
    view: { ...view, decisions: view.decisions ?? [], comments: view.comments ?? [] },
  };
}

/**
 * Say who you are — the first time you ACT, never on arrival.
 *
 * Opening a link records nothing (see migrations/027), so this is called lazily:
 * the first reaction or comment carries the join. A signed-in caller is
 * recognised as themselves across devices because the route runs `OptionalAuth`
 * and `fetchAPI` attaches a token when there is a session; an anonymous one gets
 * a fresh participant row and says their name again on the next device.
 */
export async function joinShare(
  token: string,
  displayName: string
): Promise<{ participantId: string; displayName: string }> {
  return fetchAPI<{ participantId: string; displayName: string }>(
    `/api/v1/shares/${encodeURIComponent(token)}/participants`,
    { method: 'POST', body: JSON.stringify({ displayName }) }
  );
}

/** Record where somebody landed. Answers 204, so there is nothing to read back. */
export async function setShareDecision(
  token: string,
  participantId: string,
  decision: Decision
): Promise<void> {
  await fetchAPI<void>(`/api/v1/shares/${encodeURIComponent(token)}/decision`, {
    method: 'POST',
    body: JSON.stringify({ participantId, decision }),
  });
}

/** Append to the thread. Returns the stored comment — including the name and
 *  timestamp the server settled on, which is what gets rendered. */
export async function addShareComment(
  token: string,
  participantId: string,
  body: string
): Promise<ShareComment> {
  return fetchAPI<ShareComment>(`/api/v1/shares/${encodeURIComponent(token)}/comments`, {
    method: 'POST',
    body: JSON.stringify({ participantId, body }),
  });
}

/** A share link, as its owner sees it just after minting it. */
export interface CreatedShare {
  id: string;
  token: string;
  /** The full `/shared/{token}` URL, built server-side from the configured site origin. */
  url: string;
  listingId: string;
  message?: string;
  createdAt: string;
  installUrl?: string;
}

/**
 * Mint (or re-fetch) the link for one home. **The one endpoint here that needs
 * an account**, because a share belongs to somebody — everything the recipient
 * then does does not.
 *
 * Rejects on failure, and the caller must say so: an offer of "copied!" over a
 * link that was never created is exactly the silent failure this codebase keeps
 * finding in its own share paths.
 */
export async function createShare(listingId: string, message?: string): Promise<CreatedShare> {
  return fetchAPI<CreatedShare>('/api/v1/shares', {
    method: 'POST',
    body: JSON.stringify({ listingId, message }),
    auth: true,
  });
}

// ============================================
// Activity (behaviour-derived alerts)
// ============================================

/**
 * Report what the signed-in buyer viewed and searched for.
 *
 * The input to behaviour-derived alerts: without it the server can only notify
 * people who explicitly saved a search, which measured live was one account in the
 * entire system.
 */
export async function recordActivity(
  signals: import('@/lib/activity').ActivitySignal[]
): Promise<void> {
  if (signals.length === 0) return;
  await fetchAPI<void>('/api/v1/activity', {
    method: 'POST',
    body: JSON.stringify({ signals }),
    auth: true,
  });
}
